// src/context/AuthContext.jsx
//
// Email sign-in (brainstorm-j1va). The browser holds only an httpOnly
// session cookie (__Host-fz_session) that the app never reads. GET
// /auth/session trades it for a 15-minute access token which we keep in
// memory and install as the axios Bearer default.
import React, { createContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { installRefreshInterceptor } from './refreshInterceptor.js';
import { clearLastInputs } from '../features/tasks/runInputs';

const AuthContext = createContext(null);

// Refresh this long before the access token expires.
const REFRESH_LEAD_SECONDS = 60;
// Never schedule a refresh sooner than this (guards against a tiny expires_in).
const MIN_REFRESH_DELAY_MS = 5 * 1000;
// Backoff for failed (non-401) session refreshes.
const RETRY_BASE_MS = 15 * 1000;
const RETRY_MAX_MS = 60 * 1000;
// A focus/visibility event only refreshes when the token is missing or has
// less than this long left (the pre-expiry timer handles the normal case).
// Every refresh mints a new JWT, so refreshing on each focus is wasteful.
const FOCUS_REFRESH_BELOW_SECONDS = 120;
const APIKEY = 'mysecureapikey';

function installToken(token) {
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    axios.defaults.headers.common['X-API-KEY'] = APIKEY;
  } else {
    delete axios.defaults.headers.common['Authorization'];
    delete axios.defaults.headers.common['X-API-KEY'];
  }
  axios.defaults.withCredentials = true;
}

export function AuthProvider({ children }) {
  const [token, setTokenState] = useState(null);
  const [activated, setActivated] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [user, setUser] = useState(null);
  // Bumped on every successful session fetch; re-arms the refresh timer.
  const [expiresAt, setExpiresAt] = useState(null);
  const inflight = useRef(null);
  const seq = useRef(0);
  const tokenRef = useRef(null);
  const expiresAtRef = useRef(null);
  const retryTimer = useRef(null);
  const retryAttempt = useRef(0);

  const clearSession = useCallback(() => {
    installToken(null);
    tokenRef.current = null;
    setTokenState(null);
    setIsAuthenticated(false);
    setActivated(false);
    setUser(null);
    expiresAtRef.current = null;
    setExpiresAt(null);
  }, []);

  // Adopt a freshly minted access token (session refresh or step-up).
  const setToken = useCallback((next, expiresIn) => {
    installToken(next);
    setTokenState(next);
    if (next) {
      setIsAuthenticated(true);
      if (expiresIn) {
        expiresAtRef.current = Date.now() + expiresIn * 1000;
        setExpiresAt(expiresAtRef.current);
      }
    }
  }, []);

  // Profile details beyond the session (name, org, roles, ...). Sent with
  // the in-memory Bearer, not the cookie; whatever it returns is never used
  // as a token. Best-effort.
  const refreshUser = useCallback(async (bearer) => {
    const t = bearer || tokenRef.current;
    try {
      const response = await axios.get('/user/me', {
        headers: t ? { Authorization: `Bearer ${t}` } : undefined,
      });
      setUser((prev) => ({ ...(prev || {}), ...response.data }));
      return response.data;
    } catch (error) {
      return null;
    }
  }, []);

  // GET /auth/session. Returns the session body, or null when signed out
  // or on a transient failure (which schedules a backoff retry).
  // { force: true } bypasses an in-flight request: after verify-link /
  // verify-code the cookie only exists now, so an older request must not
  // decide the outcome.
  const refreshSession = useCallback((opts = {}) => {
    if (inflight.current && !opts.force) return inflight.current;
    const mySeq = (seq.current += 1);
    const run = (async () => {
      try {
        const res = await axios.get('/auth/session', { withCredentials: true });
        if (mySeq !== seq.current) return null; // superseded
        const d = res.data || {};
        const hadToken = !!tokenRef.current;
        tokenRef.current = d.access_token;
        retryAttempt.current = 0;
        clearTimeout(retryTimer.current);
        setToken(d.access_token, d.expires_in || 900);
        setActivated(!!d.activated);
        setUser((prev) => ({ ...(prev || {}), email: d.email }));
        if (!hadToken) refreshUser(d.access_token); // first sign-in / reload
        return d;
      } catch (error) {
        if (mySeq !== seq.current) return null;
        if (error?.response?.status === 401) {
          clearTimeout(retryTimer.current);
          clearSession();
        } else {
          // Network blip / 5xx: keep what we have and retry with backoff so
          // the token is renewed before it expires.
          const delay = Math.min(
            RETRY_BASE_MS * 2 ** retryAttempt.current,
            RETRY_MAX_MS
          );
          retryAttempt.current += 1;
          clearTimeout(retryTimer.current);
          retryTimer.current = setTimeout(() => { refreshSession(); }, delay);
        }
        return null;
      } finally {
        if (mySeq === seq.current) {
          setIsCheckingAuth(false);
          inflight.current = null;
        }
      }
    })();
    inflight.current = run;
    return run;
  }, [setToken, clearSession, refreshUser]);

  // On mount.
  useEffect(() => {
    refreshSession();
    return () => clearTimeout(retryTimer.current);
  }, [refreshSession]);

  // On window focus / tab becoming visible.
  useEffect(() => {
    const onFocus = () => {
      if (document.visibilityState !== 'visible') return;
      const left = expiresAtRef.current
        ? (expiresAtRef.current - Date.now()) / 1000
        : 0;
      if (tokenRef.current && left >= FOCUS_REFRESH_BELOW_SECONDS) return;
      refreshSession();
    };
    document.addEventListener('visibilitychange', onFocus);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onFocus);
      window.removeEventListener('focus', onFocus);
    };
  }, [refreshSession]);

  // Expired-token 401: refresh once and retry the request once; sign out if
  // the refresh itself is rejected.
  useEffect(() => installRefreshInterceptor(axios, {
    refresh: async () => {
      const d = await refreshSession({ force: true });
      return d?.access_token || null;
    },
    onAuthFailure: clearSession,
  }), [refreshSession, clearSession]);

  // 60 s before the access token runs out.
  useEffect(() => {
    if (!expiresAt) return undefined;
    const delay = Math.max(
      MIN_REFRESH_DELAY_MS,
      expiresAt - Date.now() - REFRESH_LEAD_SECONDS * 1000
    );
    const id = setTimeout(() => { refreshSession(); }, delay);
    return () => clearTimeout(id);
  }, [expiresAt, refreshSession]);

  // Sign out of this browser. Send the current Bearer too: the server
  // revokes it.
  const signOut = useCallback(async () => {
    try {
      await axios.post('/auth/signout', undefined, { withCredentials: true });
    } catch (error) {
      console.error('Sign-out error:', error?.response?.status);
    }
    clearLastInputs();
    clearSession();
  }, [clearSession]);

  const signOutEverywhere = useCallback(async () => {
    try {
      await axios.post('/auth/signout-all', undefined, { withCredentials: true });
    } catch (error) {
      console.error('Sign-out-everywhere error:', error?.response?.status);
    }
    clearLastInputs();
    clearSession();
  }, [clearSession]);

  // Do NOT use useNavigate here

  return (
    <AuthContext.Provider value={{
      token,
      isAuthenticated,
      isCheckingAuth,
      activated,
      user,
      refreshSession,
      refreshUser,
      setToken,
      signOut,
      signOutEverywhere,
      // Legacy name still used by AppLayout/App.
      logout: signOut,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
